import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MessageCircleQuestion } from "lucide-react";

const faqs = [
  {
    question: "How is Reqqy different from just asking ChatGPT?",
    answer:
      "ChatGPT can help you write or rewrite text. Reqqy is built specifically to review requirements. It structures the input, checks for ambiguity, missing assumptions, and weak acceptance criteria, and returns a clearer artifact you can actually use before handoff to engineering.",
  },
  {
    question: "Who is Reqqy for?",
    answer:
      "Reqqy is built for product managers, founders, engineers, and teams who regularly turn messy ideas into requirements. It is most useful anywhere unclear specs create back-and-forth, delays, or avoidable rework.",
  },
  {
    question: "What can I put into Reqqy?",
    answer:
      "You can start with user stories, PRD excerpts, feature notes, epics, or rough requirement drafts. Reqqy works best when it has something concrete to review, even if it is incomplete or badly written.",
  },
  {
    question: "What does Reqqy actually return?",
    answer:
      "Depending on the mode, Reqqy returns a requirements artifact, a quick clarity score, and a deeper evaluation of what is unclear, incomplete, or hard to test. The goal is not just to generate text, but to help you improve requirement quality before implementation starts.",
  },
  {
    question: "Will this replace product managers?",
    answer:
      "No. Reqqy is not a replacement for product judgment. It helps with the repetitive review work around requirement quality so humans can spend more time on prioritization, trade-offs, stakeholder alignment, and decisions.",
  },
  {
    question: "How long does a review take?",
    answer:
      "Quick reviews are designed to be fast enough for early triage. Deeper evaluations take longer because they analyze ambiguity, completeness, and testability in more detail. In practice, the goal is to shorten the review loop before engineering sees the work.",
  },
  {
    question: "How does Reqqy fit into my workflow?",
    answer:
      "Use it before backlog grooming, before sprint planning, before handing a PRD to engineering, or anytime a requirement feels shaky. The simplest workflow is: bring in the requirement, review the clarity issues, tighten it, and then move forward with more confidence.",
  },
  {
    question: "What about privacy and security?",
    answer:
      "By default, Reqqy stores run data so you can revisit and improve work over time. Temporary mode is available for workflows where you do not want content retained in the normal way. We do not train models on customer content, and we are designing the product toward stronger enterprise privacy controls over time.",
  },
  {
    question: "Can I try it before committing?",
    answer:
      "Yes. There is a free starting tier so you can run your own requirements through the product and see whether the output is useful for your workflow before upgrading.",
  },
];

const faqs_old = [
  {
    question: "How is Reqqy different from just asking ChatGPT?",
    answer:
      "Reqqy is purpose-built for requirements analysis. It uses specialized prompts tuned for PM workflows, provides structured clarity scores, integrates with Jira, and offers enterprise security features like zero-retention mode. It's not a general-purpose AI—it's a focused tool for one job: making requirements clearer.",
  },
  {
    question: "Will this replace my PM role?",
    answer:
      "No. Reqqy is designed to be your junior PM assistant, not your replacement. It handles the tedious work of reviewing requirements for ambiguity so you can focus on strategy, stakeholder alignment, and decision-making. Think of it as a force multiplier.",
  },
  {
    question: "How long does it take to review requirements?",
    answer:
      "Quick analysis takes about 30 seconds. Deep analysis with improvement suggestions takes 1-2 minutes. Either way, it's faster than waiting for engineers to point out issues during sprint planning.",
  },
  {
    question: "Can I integrate with my existing tools?",
    answer:
      "Yes. Reqqy integrates with Jira for exporting improved requirements with the Reqqy-reviewed label. We're adding Aha, Confluence, Linear, and Notion integrations soon. Enterprise plans can request custom integrations.",
  },
  {
    question: "What about data privacy and security?",
    answer:
      "By default, data is stored for your convenience so you can reference past reviews. You can enable Temporary Mode for ephemeral sessions where nothing is saved. Enterprise plans offer zero-retention execution with SOC 2 Type II compliant infrastructure.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes. You receive 10 free credits monthly on the Free plan (no CC required). This gives you enough to see the value before committing to a paid plan.",
  },
];

export function FAQSection() {
  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      <div className="absolute top-[30%] left-[20%] w-[300px] h-[300px] rounded-full bg-accent/[0.03] blur-[120px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <MessageCircleQuestion className="w-3.5 h-3.5" />
            FAQ
          </div>
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">
            Frequently asked questions
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Everything you need to know about Reqqy
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="group rounded-lg glass border border-border/30 px-5 transition-all duration-300 hover:border-primary/15 data-[state=open]:border-primary/20"
              >
                <AccordionTrigger className="text-left text-sm hover:no-underline py-4 [&[data-state=open]>svg]:text-primary">
                  <span className="font-medium">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
